import { useState, useEffect, useRef, useMemo, useCallback } from 'react'

interface CardImageProps {
  src?: string | null
  alt: string
  cardId: string
  className?: string
  priority?: boolean
}

// Global cache for failed image URLs to prevent infinite retries
// Persist to localStorage with TTL so entries expire after 24 hours
const FAILED_CACHE_KEY = 'failed-image-cache-v2'
const FAILED_CACHE_TTL = 24 * 60 * 60 * 1000 // 24 hours

const loadFailedCache = (): Map<string, number> => {
  try {
    const stored = localStorage.getItem(FAILED_CACHE_KEY)
    if (!stored) return new Map()
    const entries: [string, number][] = JSON.parse(stored)
    const now = Date.now()
    // Prune expired entries on load
    return new Map(entries.filter(([, timestamp]) => now - timestamp < FAILED_CACHE_TTL))
  } catch {
    return new Map()
  }
}

const saveFailedCache = (cache: Map<string, number>) => {
  try {
    localStorage.setItem(FAILED_CACHE_KEY, JSON.stringify(Array.from(cache.entries())))
  } catch {
    // Ignore localStorage errors
  }
}

const failedImageCache = loadFailedCache()
// Clean up old format on first load
if (typeof window !== 'undefined') {
  localStorage.removeItem('failed-image-cache')
}

const isInFailedCache = (url: string): boolean => {
  const timestamp = failedImageCache.get(url)
  if (timestamp === undefined) return false
  if (Date.now() - timestamp >= FAILED_CACHE_TTL) {
    failedImageCache.delete(url)
    saveFailedCache(failedImageCache)
    return false
  }
  return true
}

const addToFailedCache = (url: string) => {
  failedImageCache.set(url, Date.now())
  saveFailedCache(failedImageCache)
}

// Expose cache clearing to window for debugging
if (typeof window !== 'undefined') {
  (window as any).clearFailedImageCache = () => {
    failedImageCache.clear()
    localStorage.removeItem(FAILED_CACHE_KEY)
    console.log('Failed image cache cleared')
  }
  (window as any).viewFailedImageCache = () => {
    const entries = Array.from(failedImageCache.entries()).map(([url, ts]) => ({
      url,
      failedAt: new Date(ts).toISOString(),
      expiresIn: Math.round((FAILED_CACHE_TTL - (Date.now() - ts)) / 60000) + 'min'
    }))
    console.log('Failed images:', entries)
    return entries
  }
}

export default function CardImage({ src, alt, cardId, className = '', priority = false }: CardImageProps) {
  const [imageError, setImageError] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [retryCount, setRetryCount] = useState(0)
  const imgRef = useRef<HTMLImageElement>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Build image URL
  // NOTE: Image caching is handled automatically by the Service Worker (public/service-worker.js)
  // - Service Worker intercepts all image fetch requests
  // - Uses cache-first strategy (cache name: 'op-card-images-v5')
  // - Images are cached automatically as they load
  // - CacheManager component can pre-populate cache for offline use
  const imageUrl = useMemo(() => {
    if (!src) return undefined

    // Already a local path or proxy URL — serve directly
    if (src.startsWith('/images/cards/') || src.startsWith('/api/images/proxy')) {
      return src
    }

    // Our own CDN: already CORS + immutable-cache + 1y max-age. Skip the
    // proxy hop (saves a redirect, and works in local dev where the proxy's
    // S3 layer isn't configured).
    if (/^https:\/\/cdn(-staging)?\.opcgct\.fraccie\.be\//.test(src)) {
      return src
    }

    // Fall back to proxy for external images
    // Service Worker will intercept and cache these too
    return `/api/images/proxy?url=${encodeURIComponent(src)}&t=${retryCount}`
  }, [src, retryCount])

  useEffect(() => {
    // Check if this image has already failed permanently
    if (src && isInFailedCache(src)) {
      setImageError(true)
      setIsLoading(false)
      return
    }

    // Reset states when src changes
    setImageError(false)
    setIsLoading(true)
    setRetryCount(0) // Reset retry count when src changes

    // Check if image is already in browser cache
    if (imageUrl) {
      const img = new Image()
      img.src = imageUrl
      if (img.complete && img.naturalHeight !== 0) {
        // Image is already cached, immediately mark as loaded
        setIsLoading(false)
      }
    }
  }, [src, imageUrl])

  const handleError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    // If this was a local image path that failed, don't retry
    if (src?.startsWith('/images/cards/')) {
      // Don't log error for missing local images - this is expected
      // console.error('Local image failed to load:', src)
      setImageError(true)
      setIsLoading(false)
      if (src) addToFailedCache(src)
      return
    }

    // If this was a cached URL attempt, try the proxy
    if (imageUrl?.startsWith('/images/cards/') && !src?.startsWith('/images/cards/') && retryCount === 0) {
      // Failed to load cached version, try proxy
      setRetryCount(1)
      setIsLoading(true)
    } else if (retryCount < 2) {
      // Try once more with a different approach
      setRetryCount(retryCount + 1)
      setIsLoading(true)
    } else {
      // All retries exhausted - add to failed cache to prevent future retries
      console.warn('Image failed to load after retries:', imageUrl)
      if (src) addToFailedCache(src)
      setImageError(true)
      setIsLoading(false)
    }
  }

  const handleLoad = useCallback(() => {
    setIsLoading(false)
    // Clear any pending timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [cardId])

  // Add timeout for stuck images and check if image is already loaded
  useEffect(() => {
    // Clear any existing timeout first
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    
    if (isLoading && imageUrl) {
      // Small delay to allow the image to start loading
      const checkTimeout = setTimeout(() => {
        // Check if the image is already loaded (browser cache)
        if (imgRef.current && imgRef.current.complete && imgRef.current.naturalHeight !== 0) {
          setIsLoading(false)
          return
        }
        
        // Set a timeout to hide the spinner after 3 seconds
        timeoutRef.current = setTimeout(() => {
          if (isLoading) { // Double check it's still loading
            // Final check if image loaded
            if (imgRef.current && imgRef.current.complete && imgRef.current.naturalHeight !== 0) {
              setIsLoading(false)
            } else {
              // Hide spinner after timeout - especially for alt art cards that might take longer
              setIsLoading(false)
            }
          }
        }, 3000) // 3 second timeout for better UX
      }, 100) // 100ms delay to let image start loading
      
      return () => {
        clearTimeout(checkTimeout)
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current)
          timeoutRef.current = null
        }
      }
    }
  }, [isLoading, imageUrl, cardId])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  if (!src || imageError) {
    return (
      <div className={`bg-gray-200 flex items-center justify-center ${className}`}>
        <div className="text-center p-4">
          <svg 
            className="w-12 h-12 mx-auto text-gray-400 mb-2" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path 
              strokeLinecap="round" 
              strokeLinejoin="round" 
              strokeWidth={2} 
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" 
            />
          </svg>
          <p className="text-gray-500 text-xs">No Image</p>
          <p className="text-gray-400 text-xs mt-1">{cardId}</p>
        </div>
      </div>
    )
  }

  return (
    <div className={`relative ${className}`}>
      {/* Show loading spinner while image is loading */}
      {isLoading && !imageError && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
          <div className="text-gray-400">
            <svg className="animate-spin h-8 w-8" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        </div>
      )}
      {/* Always render img — browser's native loading="lazy" handles viewport-based deferral */}
      {!imageError && (
        <img
          ref={imgRef}
          key={`${src}-${retryCount}`}
          src={imageUrl}
          alt={alt}
          className={`w-full h-full object-cover ${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
          onError={handleError}
          onLoad={handleLoad}
          onLoadedData={handleLoad}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
        />
      )}
    </div>
  )
}